import { useCallback, useEffect, useState } from 'react';
import type { WidgetConfig } from '../lib/config';
import { login, register } from '../lib/api';
import { t } from '../lib/strings';
import { BeforeAfter } from './BeforeAfter';
import { Disclaimer } from './Disclaimer';

type Phase = 'info' | 'auth';
type Mode = 'login' | 'register';

// Logged-out gate: an info section (before/after demo + explanation) first, then a
// single-form auth panel with a Login/Register toggle. Only one form is in the DOM at
// a time (correct autocomplete attrs) so password managers see one coherent form.
// On success the WP auth cookie is set, so we reload — the page then renders logged-in.
export function LoginModal({ config, onClose }: { config: WidgetConfig; onClose: () => void }) {
  const [phase, setPhase] = useState<Phase>('info');
  const [mode, setMode] = useState<Mode>('login');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [hp, setHp] = useState(''); // honeypot — must stay empty for humans
  const [marketingOptIn, setMarketingOptIn] = useState(false); // SPEZ-14 — opt-in, default OFF

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const goAuth = useCallback((m: Mode) => {
    setMode(m);
    setError(null);
    setBusy(false);
    setPhase('auth');
  }, []);

  const switchMode = useCallback((m: Mode) => {
    setMode(m);
    setError(null);
    setBusy(false);
  }, []);

  const onLoginSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!username || !password) return;
      setBusy(true);
      setError(null);
      const res = await login(config, username, password);
      if (res.ok) {
        window.location.reload();
        return;
      }
      setError(res.message ?? t('login_error'));
      setBusy(false);
    },
    [config, username, password],
  );

  const onRegisterSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email || !regPassword) return;
      setBusy(true);
      setError(null);
      const res = await register(config, email, regPassword, hp, marketingOptIn);
      if (res.ok) {
        window.location.reload();
        return;
      }
      setError(res.message ?? t('register_error'));
      setBusy(false);
    },
    [config, email, regPassword, hp, marketingOptIn],
  );

  const headTitle =
    phase === 'info' ? t('info_title') : mode === 'login' ? t('login_title') : t('register_title');

  return (
    <div className="aiw-overlay" onClick={onClose} role="presentation">
      <div
        className="aiw-dialog aiw-dialog-narrow"
        role="dialog"
        aria-modal="true"
        aria-label={headTitle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aiw-dialog-head">
          <h2>{headTitle}</h2>
          <button type="button" className="aiw-close" aria-label={t('close')} onClick={onClose}>
            ×
          </button>
        </div>

        {phase === 'info' ? (
          <div className="aiw-body">
            <BeforeAfter before={config.demoBefore} after={config.demoAfter} />
            <p className="aiw-muted">
              {t('info_login')}{' '}
              {t('info_quota', {
                limit: config.quotaLimit,
                period: t(config.quotaPeriod === 'weekly' ? 'period_weekly' : 'period_monthly'),
              })}
            </p>
            <Disclaimer url={config.privacyUrl} label={config.privacyLabel} />
            <div className="aiw-actions">
              <button type="button" className="aiw-btn" onClick={() => goAuth('login')}>
                {t('info_continue')}
              </button>
            </div>
            {config.registrationEnabled && (
              <p className="aiw-muted aiw-small aiw-register">
                {t('register_prompt')}{' '}
                <button type="button" className="aiw-link" onClick={() => goAuth('register')}>
                  {t('register_cta')}
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="aiw-body">
            {config.registrationEnabled && (
              <div className="aiw-tabs" role="tablist" aria-label={t('login_title')}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'login'}
                  className={`aiw-tab ${mode === 'login' ? 'is-active' : ''}`}
                  onClick={() => switchMode('login')}
                >
                  {t('auth_tab_login')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'register'}
                  className={`aiw-tab ${mode === 'register' ? 'is-active' : ''}`}
                  onClick={() => switchMode('register')}
                >
                  {t('auth_tab_register')}
                </button>
              </div>
            )}

            {mode === 'login' ? (
              <form className="aiw-form" onSubmit={onLoginSubmit}>
                <label className="aiw-field">
                  <span className="aiw-label">{t('login_username')}</span>
                  <input
                    className="aiw-input"
                    type="text"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoFocus
                  />
                </label>
                <label className="aiw-field">
                  <span className="aiw-label">{t('login_password')}</span>
                  <input
                    className="aiw-input"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </label>

                {error && <p className="aiw-error">{error}</p>}

                <div className="aiw-actions">
                  <button type="submit" className="aiw-btn" disabled={busy || !username || !password}>
                    {busy ? t('login_busy') : t('login_submit')}
                  </button>
                </div>
              </form>
            ) : (
              <form className="aiw-form" onSubmit={onRegisterSubmit}>
                <label className="aiw-field">
                  <span className="aiw-label">{t('register_email')}</span>
                  <input
                    className="aiw-input"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                  />
                </label>
                <label className="aiw-field">
                  <span className="aiw-label">{t('register_password')}</span>
                  <input
                    className="aiw-input"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                  />
                  <span className="aiw-muted aiw-small">{t('register_password_hint')}</span>
                </label>

                {/* SPEZ-14 — optional marketing opt-in (unchecked by default). */}
                <label className="aiw-field aiw-checkbox">
                  <input
                    type="checkbox"
                    checked={marketingOptIn}
                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                  />
                  {config.marketingOptinHtml ? (
                    // Admin-configured label (server-side wp_kses-sanitized HTML, {link} expanded).
                    <span
                      className="aiw-small"
                      dangerouslySetInnerHTML={{ __html: config.marketingOptinHtml }}
                    />
                  ) : (
                    <span className="aiw-small">
                      {t('marketing_optin_pre')}
                      <a href={config.privacyUrl} target="_blank" rel="noopener noreferrer">
                        {t('marketing_optin_link')}
                      </a>
                      {t('marketing_optin_post')}
                    </span>
                  )}
                </label>

                {/* Honeypot — hidden from humans; bots that fill it are rejected. */}
                <input
                  className="aiw-visually-hidden"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={hp}
                  onChange={(e) => setHp(e.target.value)}
                />

                {error && <p className="aiw-error">{error}</p>}

                <div className="aiw-actions">
                  <button type="submit" className="aiw-btn" disabled={busy || !email || !regPassword}>
                    {busy ? t('register_busy') : t('register_submit')}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
